/**
 * Recurrence helpers — RRULE expansion, deterministic IDs, occurrence key computation.
 *
 * Used by both client (after-completion generation) and server (CF DO cron generation).
 */

import { v5 as uuidv5 } from "uuid"
import * as rruleModule from "rrule"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc.js"
import timezone from "dayjs/plugin/timezone.js"

// rrule CJS/ESM interop: handle both Vite (named exports) and Node ESM (default.RRule)
const RRule: typeof rruleModule.RRule =
  (rruleModule as unknown as { default?: { RRule: typeof rruleModule.RRule } }).default?.RRule ??
  rruleModule.RRule

const rrulestr: typeof rruleModule.rrulestr =
  (rruleModule as unknown as { default?: { rrulestr: typeof rruleModule.rrulestr } }).default?.rrulestr ??
  rruleModule.rrulestr

dayjs.extend(utc)
dayjs.extend(timezone)

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Namespace UUID for ToToDo recurrence (hardcoded, never changes).
 * Used with UUID v5 to produce deterministic instance IDs.
 */
export const RECURRENCE_NS = "7f3b8c2a-1d4e-4a9f-b6c8-3e5f7a2d9b0c"

/** User-status value for "skipped" instances (distinguishes from manual cancel) */
export const RECURRENCE_SKIPPED = "skipped"

// ---------------------------------------------------------------------------
// Deterministic ID
// ---------------------------------------------------------------------------

/**
 * Produce a deterministic UUID v5 for a recurring instance.
 * Same input → same output, always. Compatible with Dexie Cloud (standard UUID format).
 */
export function deterministicInstanceId(templateId: string, occurrenceKey: string): string {
  return uuidv5(`${templateId}|${occurrenceKey}`, RECURRENCE_NS)
}

// ---------------------------------------------------------------------------
// Occurrence Key
// ---------------------------------------------------------------------------

/**
 * Compute occurrence key for FIXED mode from a UTC ms timestamp.
 * Format: "YYYYMMDD" in the template's timezone.
 */
export function computeOccurrenceKey(utcMs: number, tz: string): string {
  return dayjs(utcMs).tz(tz).format("YYYYMMDD")
}

/**
 * Compute occurrence key for AFTER_COMPLETION mode.
 * Format: "seq:N" (sequential number).
 */
export function computeAfterCompletionKey(seqNumber: number): string {
  return `seq:${seqNumber}`
}

// ---------------------------------------------------------------------------
// RRULE Expansion
// ---------------------------------------------------------------------------

export interface OccurrenceDate {
  /** Date string "YYYY-MM-DD" in the template's timezone */
  dateStr: string
  /** UTC ms timestamp (with correct DST offset applied) */
  utcMs: number
  /** Occurrence key for dedup */
  occurrenceKey: string
}

/**
 * Expand RRULE occurrences within a date range.
 *
 * Strategy: date-only RRULE expansion + dayjs.tz() for correct DST handling.
 * The RRULE is expanded as "noon UTC" dates to avoid DST edge cases,
 * then each date is converted to the correct local time.
 *
 * @param rruleString Full RRULE string (may include DTSTART line)
 * @param tz IANA timezone (e.g. "Europe/Stockholm")
 * @param from Start of range (UTC ms)
 * @param to End of range (UTC ms)
 * @returns Array of occurrence dates within the range
 */
export function expandRruleOccurrences(
  rruleString: string,
  tz: string,
  from: number,
  to: number,
): OccurrenceDate[] {
  // Use rrulestr for multi-line strings (DTSTART + RRULE), RRule.fromString for single RRULE
  const rule = rruleString.includes("\n")
    ? rrulestr(rruleString)
    : RRule.fromString(rruleString)
  const fromDate = new Date(from)
  const toDate = new Date(to)

  // Extract time-of-day from DTSTART (if present) to apply to each occurrence.
  // Without this, occurrences default to 00:00 local time and reminders fire at wrong times.
  let dtstartHour = 0
  let dtstartMinute = 0
  const dtstartMatch = rruleString.match(/DTSTART[^:]*:(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z?/)
  if (dtstartMatch) {
    // DTSTART is in UTC — convert to local timezone to get the intended local time
    const utcMs = Date.UTC(
      Number(dtstartMatch[1]), Number(dtstartMatch[2]) - 1, Number(dtstartMatch[3]),
      Number(dtstartMatch[4]), Number(dtstartMatch[5]), Number(dtstartMatch[6]),
    )
    const localDt = dayjs(utcMs).tz(tz)
    dtstartHour = localDt.hour()
    dtstartMinute = localDt.minute()
  }

  // Expand dates (inclusive on both ends)
  const rawDates = rule.between(fromDate, toDate, true)

  return rawDates.map(d => {
    // Extract date-only portion (avoid DST issues from rrule lib)
    const dateStr = d.toISOString().slice(0, 10) // "2026-03-29"
    // Apply correct timezone offset using dayjs, preserving DTSTART time-of-day
    const localDt = dayjs.tz(dateStr, tz).hour(dtstartHour).minute(dtstartMinute)
    const utcMs = localDt.utc().valueOf()
    const occurrenceKey = dateStr.replace(/-/g, "") // "20260329"

    return { dateStr, utcMs, occurrenceKey }
  })
}

// ---------------------------------------------------------------------------
// After-Completion: Next Date
// ---------------------------------------------------------------------------

/**
 * Compute the next occurrence date for AFTER_COMPLETION mode.
 *
 * @param afterCompletionDays Days after completion to generate next (default: 1)
 * @param afterCompletionTime Optional time "HH:mm" to set on target day
 * @param completedAtMs When the instance was completed (UTC ms)
 * @param tz Template timezone
 * @returns UTC ms for the next instance's startAt, or null if end conditions met
 */
export function computeNextAfterCompletionDate(
  afterCompletionDays: number,
  afterCompletionTime: string | undefined,
  completedAtMs: number,
  tz: string,
): number {
  let nextDate = dayjs(completedAtMs).tz(tz).add(afterCompletionDays, "day").startOf("day")

  if (afterCompletionTime) {
    const [h, m] = afterCompletionTime.split(":").map(Number)
    nextDate = nextDate.hour(h!).minute(m!)
  }

  return nextDate.utc().valueOf()
}

// ---------------------------------------------------------------------------
// Human-readable rule summary
// ---------------------------------------------------------------------------

/**
 * Generate a human-readable summary of a recurrence rule.
 * Used for display on task cards and in the recurrence picker.
 */
export function humanReadableRule(
  rruleString: string | undefined,
  mode: "FIXED" | "AFTER_COMPLETION",
  afterCompletionDays?: number,
  locale = "en",
): string {
  if (mode === "AFTER_COMPLETION") {
    const days = afterCompletionDays ?? 1
    if (locale === "sv") {
      return days === 1 ? "1 dag efter slutförd" : `${days} dagar efter slutförd`
    }
    return days === 1 ? "1 day after completion" : `${days} days after completion`
  }

  if (!rruleString) return ""

  try {
    const rule = RRule.fromString(rruleString)
    return rule.toText()
  } catch {
    return rruleString
  }
}

// ---------------------------------------------------------------------------
// Parse RRULE → config object
// ---------------------------------------------------------------------------

/** Frequency string literals used by RecurrenceConfig */
export type ParsedFrequency = "daily" | "weekly" | "monthly" | "yearly"

/** Result of parsing an RRULE string into structured config fields */
export interface ParsedRruleConfig {
  frequency: ParsedFrequency
  interval: number
  weekdays: number[]         // 0=MO, 1=TU, ... 6=SU
  monthDay?: number
  monthWeek?: number         // 1,2,3,4,-1 (bysetpos)
  monthWeekday?: number      // 0=MO...6=SU
}

const FREQ_MAP: Record<number, ParsedFrequency> = {
  [RRule.DAILY]: "daily",
  [RRule.WEEKLY]: "weekly",
  [RRule.MONTHLY]: "monthly",
  [RRule.YEARLY]: "yearly",
}

/**
 * Parse an RRULE string into structured frequency/interval/weekday config.
 *
 * Central parser — use this everywhere instead of ad-hoc RRule.fromString().
 * Returns a default "weekly" config on parse failure.
 */
export function parseRruleToConfig(rruleString: string): ParsedRruleConfig {
  try {
    const rule = RRule.fromString(rruleString)
    const result: ParsedRruleConfig = {
      frequency: FREQ_MAP[rule.options.freq] ?? "weekly",
      interval: rule.options.interval ?? 1,
      weekdays: [],
    }

    if (rule.options.byweekday?.length) {
      result.weekdays = rule.options.byweekday.map((d: number | { weekday: number; n?: number }) =>
        typeof d === "number" ? d : d.weekday
      )
    }

    if (rule.options.bymonthday?.length) {
      result.monthDay = rule.options.bymonthday[0]
    }

    if (rule.options.bysetpos?.length && rule.options.byweekday?.length) {
      result.monthWeek = rule.options.bysetpos[0]
      const wd = rule.options.byweekday[0]
      result.monthWeekday = typeof wd === "number" ? wd : (wd as unknown as { weekday: number }).weekday
    }

    return result
  } catch {
    return { frequency: "weekly", interval: 1, weekdays: [] }
  }
}
